import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import styled from "styled-components";
import {Icon} from '@blueprint/ui';

import {
    ActivityLogResponse,
    UpdateDetails,
} from "../../../api/userActivityLogs";
import { NoRecordCard } from "../../../common/components";
import { Table } from "../../../common/components/core";
import { ResponseWithPagination } from "../../../types/common";
import { UserActivityLogActionType } from "../UserActivityLogView";
import { toDateTimeString } from "../utils/dateTime";
import { parseRoleToText } from "../../../utils/role";
import { generateTypeDisplayName } from "../../EntityManagement/ProfileManageMentView";

export type UserActivityLogState = {
    id?: number;
    actionType: string;
    userInfo: string;
    updateDetails?: UpdateDetails[];
    actionTakerInfo: string;
    timestamp: string;
};

type UserActivityLogListProps = {
    activityLogs: ResponseWithPagination<ActivityLogResponse[]>;
    nextPage: () => void;
    prevPage: () => void;
    toPage: (page: number) => void;
};

const UserActivityLogList = ({
    activityLogs,
    nextPage,
    prevPage,
    toPage,
}: UserActivityLogListProps): JSX.Element => {
    const [activityLogParsedContent, setActivityLogParsedContent] = useState<
        UserActivityLogState[]
    >([]);

    const formatActionType = (type: string): string => {
        switch (type) {
            case "Created User":
                return UserActivityLogActionType.CREATED_USER;
            case "Updated User":
                return UserActivityLogActionType.UPDATED_USER;
            case "Deactivated User":
                return UserActivityLogActionType.DELETED_USER;
            default:
                return type;
        }
    };

    useEffect(() => {
        setActivityLogParsedContent(
            activityLogs.content.map((log) => {
                return {
                    id: log.id || undefined,
                    actionType: formatActionType(log.activityType),
                    userInfo: log.changedUserInfo,
                    updateDetails: log.updateDetails,
                    actionTakerInfo: `${log.actionedUser.fullName} (${log.actionedUser.email})`,
                    timestamp: log.activityTimestamp,
                };
            }),
        );
    }, [activityLogs.content]);

    return !activityLogParsedContent ||
        activityLogParsedContent.length === 0 ? (
        <NoRecordCard />
    ) : (
        <Card className="mt-4 border-0">
            <Card.Body className="px-0 py-0">
                <table className="custom-table">
                    <thead>
                        <tr>
                            <Table.HeaderCell width={460} className="pt-3">
                                Activity
                            </Table.HeaderCell>
                            <Table.HeaderCell width={280} className="pt-3">
                                Action Taker
                            </Table.HeaderCell>
                            <Table.HeaderCell width={180} className="pt-3">
                                Timestamp
                            </Table.HeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        {activityLogParsedContent.map((log) => (
                            <UserActivityLog key={log.id} log={log} />
                        ))}
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colSpan={3}>
                            <Table.Pagination
                                backArrowOnClick={prevPage}
                                nextArrowOnClick={nextPage}
                                backArrowDisabled={activityLogs.first}
                                nextArrowDisabled={activityLogs.last}
                                totalPage={activityLogs.totalPages}
                                currentPage={activityLogs.number + 1}
                                onPageNumberChange={(page) => toPage(page - 1)}
                            />
                        </td>
                    </tr>
                    </tfoot>
                </table>
            </Card.Body>
        </Card>
    );
};

type UserActivityLogProps = {
    log: UserActivityLogState;
};

const UserActivityLog = ({ log }: UserActivityLogProps) => {
    const isUpdatedUser =
        log.actionType === UserActivityLogActionType.UPDATED_USER;
    const [selected, setSelected] = useState(false);
    const Arrows = () => (selected ? <Icon titleAccess={'ChevronUp'} name={'ChevronUp'} /> : <Icon titleAccess={'ChevronDown'} name={'ChevronDown'} />);

    const updateFieldToText = (field: string) => {
        switch (field) {
            case "isEnableInvEmailNotif":
                return "Email Notification";
            case "IsProfilemanagementEnabled":
                return "Profile Management";
            case "IsUsermanagementEnabled":
                return "User Management";
            case "role":
                return "Role Type";
            case "status":
                return "Account status";
            case "firstName":
                return "First Name";
            case "lastName":
                return "Last Name";
            case "profileName":
                return "Profile Name";
            case "profileType":
                return "Profile Type";
            default:
                return field;
        }
    };

    const isBoolField = (field: string) =>
        [
            "isEnableInvEmailNotif",
            "IsProfilemanagementEnabled",
            "IsUsermanagementEnabled",
        ].includes(field);

    return (
        <Table.BodyRow
            onClick={() => {
                setSelected((prevValue) => !prevValue);
            }}
            onKeyUp={(event:KeyboardEvent) =>
                {(event.keyCode === 13 || event.keyCode === 32) &&
                    setSelected((prevValue) => !prevValue);
                }
            }
        >
            <StyledTd>
                <StyledActivity>
                    <div>
                        {log.actionType}{" "}
                        <span>{log.userInfo}</span>
                    </div>
                    {selected &&
                        isUpdatedUser &&
                        log.updateDetails &&
                        log.updateDetails.map(
                            ({ field, oldValue, newValue }, index) =>
                                isBoolField(field) ? (
                                    <BoolUpdatedValue
                                        field={updateFieldToText(field)}
                                        newValue={newValue}
                                        key={index}
                                    />
                                ) : (
                                    <NormalUpdatedValue
                                        key={index}
                                        field={updateFieldToText(field)}
                                        oldValue={oldValue}
                                        newValue={newValue}
                                    />
                                ),
                        )}
                </StyledActivity>
            </StyledTd>
            <StyledTd>{log.actionTakerInfo}</StyledTd>
            <StyledTd>
                <StyledTimestamp>
                    <p>{toDateTimeString(log.timestamp)}</p>
                    {isUpdatedUser &&
                    log.updateDetails &&
                    log.updateDetails.length ? (
                        <Arrows />
                    ) : null}
                </StyledTimestamp>
            </StyledTd>
        </Table.BodyRow>
    );
};

const StyledTd = styled.td`
    vertical-align: top;
`;

const StyledActivity = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    row-gap: 5px;
`;

const StyledTimestamp = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    p {
        margin: 0px;
    }
`;

const NormalUpdatedValue = ({ field, oldValue, newValue }: UpdateDetails) => {
    const getUpdatedFieldValueText = (value: string) =>
        parseRoleToText(value) || generateTypeDisplayName(value) || value;

    return (
        <div>
            {`${field} changed from `}
            <span>
                {getUpdatedFieldValueText(oldValue)}
            </span>{" "}
            to{" "}
            <span>
                {getUpdatedFieldValueText(newValue)}
            </span>
            .
        </div>
    );
};

const BoolUpdatedValue = ({
    field,
    newValue,
}: Omit<UpdateDetails, "oldValue">) => {
    return (
        <p>
            {field} {newValue === "true" ? "enabled" : "disabled"}
        </p>
    );
};

export default UserActivityLogList;
